/**
 * BarcodeGenerator utility
 * Generates barcodes using jsbarcode library
 * Supports variable-length numeric and alphanumeric data
 * Produces clear, professional, scannable barcodes
 */

import JsBarcode from 'jsbarcode'

class BarcodeGenerator {
  /**
   * Generate a barcode canvas element
   * @param {string|number} data - The data to encode (any length, numeric or alphanumeric)
   * @param {object} options - Configuration options
   * @param {number} options.width - Canvas width in pixels (default: 2)
   * @param {number} options.height - Canvas height in pixels (default: 50)
   * @param {string} options.format - Barcode format (default: 'CODE128')
   * @returns {HTMLCanvasElement} - Canvas element with the barcode
   */
  generateBarcodeCanvas(data, options = {}) {
    const {
      width = 2,
      height = 50,
      format = 'CODE128'
    } = options

    const canvas = document.createElement('canvas')

    // Use JsBarcode to render directly to the canvas
    try {
      JsBarcode(canvas, String(data), {
        format: format,
        width: width,
        height: height,
        displayValue: false, // Don't display the text below the barcode
        margin: 5
      })
    } catch (error) {
      console.error('Error generating barcode:', error)
      throw error
    }

    return canvas
  }

  /**
   * Generate barcode as data URL (base64 encoded image)
   * @param {string|number} data - The data to encode (any length)
   * @param {object} options - Configuration options
   * @returns {string} - Data URL of the barcode image
   */
  generateBarcodeDataURL(data, options = {}) {
    const canvas = this.generateBarcodeCanvas(data, options)
    return canvas.toDataURL('image/png')
  }

  /**
   * Generate barcode as blob for direct file usage
   * @param {string|number} data - The data to encode (any length)
   * @param {object} options - Configuration options
   * @returns {Promise<Blob>} - Blob of the barcode image
   */
  async generateBarcodeBlob(data, options = {}) {
    return new Promise((resolve, reject) => {
      try {
        const canvas = this.generateBarcodeCanvas(data, options)
        canvas.toBlob(resolve, 'image/png')
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Generate barcode as SVG
   * @param {string|number} data - The data to encode (any length)
   * @param {object} options - Configuration options
   * @returns {string} - SVG string of the barcode
   */
  generateBarcodeSVG(data, options = {}) {
    const {
      width = 2,
      height = 50,
      format = 'CODE128'
    } = options

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')

    try {
      JsBarcode(svg, String(data), {
        format: format,
        width: width,
        height: height,
        displayValue: false,
        margin: 5
      })
    } catch (error) {
      console.error('Error generating barcode:', error)
      throw error
    }

    return new XMLSerializer().serializeToString(svg)
  }
}

export default new BarcodeGenerator()
